import type {
  IntegrationSource,
  OrgAppConnectionPayload,
  UpsertContactViewConfigPayload,
} from 'v2/types/api/settings'
import type { BridgeConnectOptions, RegisterCustomMcpPayload } from './aci-integrations-service'
import { aciIntegrationsService } from './aci-integrations-service'
import {
  type CustomField,
  type ExecuteActionPayload,
  externalService,
  type SetOAuthCredentialsPayload,
} from './external-apps-service'

const sourceOrLegacy = (source?: IntegrationSource): IntegrationSource => source ?? 'nango'

export const createAciBrowserExecutionUnavailableError = (
  capability = 'Manual integration actions',
) =>
  Object.assign(
    new Error(`${capability} are unavailable while the new integrations service is enabled`),
    {
      name: 'AciBrowserExecutionUnavailableError',
    },
  )

/**
 * One routing decision for every per-connection capability: a connection's own
 * `source` outranks the global flag (so a rolled-back org keeps working), and an
 * ACI-enabled org may not drive a legacy connection from the browser at all.
 */
const routeBySource = <TAci, TLegacy>(
  source: IntegrationSource | undefined,
  aciEnabled: boolean,
  capability: string,
  aciFn: () => Promise<TAci>,
  legacyFn: () => Promise<TLegacy>,
): Promise<TAci | TLegacy> => {
  const resolved = sourceOrLegacy(source)
  if (aciEnabled && resolved !== 'aci') {
    return Promise.reject(createAciBrowserExecutionUnavailableError(capability))
  }
  return resolved === 'aci' ? aciFn() : legacyFn()
}

interface IntegrationSourceGatewayOptions {
  aciEnabled?: boolean
  aciService?: typeof aciIntegrationsService
  legacyService?: typeof externalService
}

export const createIntegrationSourceGateway = ({
  aciEnabled = false,
  aciService = aciIntegrationsService,
  legacyService = externalService,
}: IntegrationSourceGatewayOptions = {}) => ({
  getCatalog: () => (aciEnabled ? aciService.getCatalog() : legacyService.getExternalAppsV2()),
  getAciConnections: () => aciService.getConnections(),
  getLegacyConnections: (isActive?: boolean) => legacyService.getExternalOrgAppsV3(isActive),
  connectAci: (appName: string, afterRedirectUrl: string, options: BridgeConnectOptions) =>
    aciService.connect(appName, afterRedirectUrl, options),
  createLegacyConnection: (payload: OrgAppConnectionPayload) =>
    aciEnabled
      ? Promise.reject(createAciBrowserExecutionUnavailableError('Legacy connection creation'))
      : legacyService.createOrgAppConnectionV2(payload),
  registerCustomMcp: (payload: RegisterCustomMcpPayload) => aciService.registerCustomMcp(payload),
  updateConnection: (
    source: IntegrationSource | undefined,
    appId: string,
    payload: OrgAppConnectionPayload,
  ) =>
    routeBySource(
      source,
      aciEnabled,
      'Legacy connection updates',
      () => aciService.updateConnection(appId, { enabled: payload.enabled }),
      () => legacyService.updateOrgAppConnectionV2(payload, appId),
    ),
  deleteConnection: (source: IntegrationSource | undefined, appId: string) =>
    routeBySource(
      source,
      aciEnabled,
      'Legacy connection deletion',
      () => aciService.deleteConnection(appId),
      () => legacyService.deleteOrgAppConnection(appId),
    ),
  reconnect: (source: IntegrationSource | undefined, appId: string, afterRedirectUrl?: string) =>
    routeBySource(
      source,
      aciEnabled,
      'Legacy connection reconnection',
      () => aciService.reconnect(appId, afterRedirectUrl),
      () => legacyService.reconnectOrgAppConnection(appId),
    ),
  getCustomFields: (source: IntegrationSource | undefined, appId: string, entities: string[]) =>
    routeBySource(
      source,
      aciEnabled,
      'Legacy custom field management',
      () => aciService.getCustomFields(appId, entities),
      () => legacyService.getCustomFields(appId, entities),
    ),
  updateCustomFields: (
    source: IntegrationSource | undefined,
    appId: string,
    fieldsByEntity: Record<string, CustomField[]>,
  ) =>
    routeBySource(
      source,
      aciEnabled,
      'Legacy custom field management',
      () => aciService.updateCustomFields(appId, fieldsByEntity),
      () => legacyService.updateCustomFields(appId, fieldsByEntity),
    ),
  resyncCustomFields: (source: IntegrationSource | undefined, appId: string, entities: string[]) =>
    routeBySource(
      source,
      aciEnabled,
      'Legacy custom field management',
      () => aciService.resyncCustomFields(appId, entities),
      () => legacyService.resyncCustomFields(appId, entities),
    ),
  grantAccess: (
    source: IntegrationSource | undefined,
    appId: string,
    payload?: Record<string, unknown>,
  ) =>
    routeBySource(
      source,
      aciEnabled,
      'Legacy access management',
      () => aciService.grantAccess(appId, payload),
      () => legacyService.grantAccess(appId, payload),
    ),
  revokeAccess: (
    source: IntegrationSource | undefined,
    appId: string,
    payload?: Record<string, unknown>,
  ) =>
    routeBySource(
      source,
      aciEnabled,
      'Legacy access management',
      () => aciService.revokeAccess(appId, payload),
      () => legacyService.revokeAccess(appId, payload),
    ),
  getAccessRecords: (source: IntegrationSource | undefined, appId: string) =>
    routeBySource(
      source,
      aciEnabled,
      'Legacy access management',
      () => aciService.getAccessRecords(appId),
      () =>
        // The legacy endpoint returns only a user list. `owner` and `org` are
        // OMITTED rather than defaulted: the caller falls back to
        // `app.integrated_by?.id` for the owner, and an absent `org` must not be
        // read as "explicitly not shared with the organization".
        legacyService
          .getAccessRecords(appId)
          .then((response) => ({
            ...response,
            data: {
              users: response.data.map(({ id }) => id),
            },
          })),
    ),
  getOAuthProviders: () =>
    aciEnabled ? aciService.getOAuthProviders() : legacyService.getOAuthProviders(),
  setOAuthCredentials: (
    provider: string,
    payload: Omit<SetOAuthCredentialsPayload, 'app_provider'>,
  ) =>
    aciEnabled
      ? aciService.setOAuthCredentials(provider, payload)
      : legacyService.setOAuthCredentials({ app_provider: provider, ...payload }),
  getContactViewConfig: () =>
    aciEnabled ? aciService.getContactViewConfig() : legacyService.getContactViewConfig(),
  upsertContactViewConfig: (payload: UpsertContactViewConfigPayload) =>
    aciEnabled
      ? aciService.upsertContactViewConfig(payload)
      : legacyService.upsertContactViewConfig(payload),
  executeManualAction: (payload: ExecuteActionPayload) => {
    if (aciEnabled) throw createAciBrowserExecutionUnavailableError()
    return legacyService.executeAction(payload)
  },
  getAppSummary: (appId: string, contactIdentifier: string) => {
    if (aciEnabled) throw createAciBrowserExecutionUnavailableError()
    return legacyService.getAppSummary(appId, contactIdentifier)
  },
  getContactSummary: (contactIdentifier: string) => {
    if (aciEnabled) throw createAciBrowserExecutionUnavailableError()
    return legacyService.getContactSummary(contactIdentifier)
  },
})

export type IntegrationSourceGateway = ReturnType<typeof createIntegrationSourceGateway>
